<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" md="8" class="text-justify">
        <h2 class="display-2 text-center text-secondary">Holoword</h2>
        <p class="text-body-1">
          A Bothan researcher is exploring the Galactic Library, seeking hidden fragments of
          knowledge to enrich the Galactic Encyclopedia. Can you help uncover the secrets buried within? Use
          your intuition to find the secret word, and bring valuable data back to the galaxy! A new word every day!
        </p>
      </v-col>
    </v-row>
    <!-- Game Section -->
    <v-row justify="center">
      <v-col cols="12" md="8" class="text-center">
        <v-card class="pa-4 text-center">
          <!-- Input and Submit -->
          <v-text-field v-model="guess" placeholder="Enter your guess..." outlined hide-details
            @keydown.enter="submitGuess"></v-text-field>
          <v-btn color="primary" @click="submitGuess" class="mt-2">Submit</v-btn>
          <!-- Feedback Message -->
          <p v-if="feedbackMessage" class="mt-3 text-success">{{ feedbackMessage }}</p>
          <!-- Last Guess -->
          <div v-if="lastGuess" class="mt-3">
            <h3 class="text-h6">Last Guess:</h3>
            <v-list-item :style="{ backgroundColor: getColor(lastGuess.rank) }">
              <v-list-item-content>
                <v-list-item-title>
                  <strong>{{ lastGuess.word }}</strong>
                </v-list-item-title>
                <v-list-item-subtitle>
                  Rank: {{ lastGuess.rank !== null ? lastGuess.rank : 'Not in top 1000' }} ({{ lastGuess.proximity }})
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </div>
          <!-- Guess History -->
          <div v-if="lastGuess" class="mt-2">
            <h3 class="text-h6">Past Guesses:</h3>
            <v-list>
              <v-list-item v-for="(entry, index) in sortedGuessHistory" :key="index" :style="{ backgroundColor: getColor(entry.rank) }">
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>{{ entry.word }}</strong>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    Rank: {{ entry.rank !== null ? entry.rank : 'Not in top 1000' }} ({{ entry.proximity }})
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
          <!-- Hint or Secret Word -->
          <div class="mt-5">
            <v-btn color="secondary" @click="revealSecret">Give Up</v-btn>
            <p v-if="revealed" class="mt-4">The secret word was: <strong>{{ secretWord.secret }}</strong></p>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import apiClient from "@/apiClient";
import { useHead } from '@unhead/vue';

export default {
  data() {
    return {
      guess: "",
      rank: null,
      proximity: null,
      revealed: false,
      secretWord: "",
      lastGuess: null,
      guessHistory: [],
      feedbackMessage: "",
      initializationMessage: "", // Message after initialization
    };
  },
  setup() {
    useHead({
      title: "Holoword – Daily Star Wars Word Puzzle Game | Test Your Lore Knowledge",
      meta: [
        {
          name: "description",
          content: "Play Holoword, the daily Star Wars word puzzle! Uncover hidden galactic terms, test your intuition, and help expand the Holonet Galactic Encyclopedia. Can you guess today’s secret word?",
        },
        {
          property: "og:title",
          content: "Holoword – Daily Star Wars Word Puzzle Game",
        },
        {
          property: "og:description",
          content: "A Bothan researcher needs your help! Solve the daily Holoword puzzle to uncover the secret Star Wars word hidden in the Galactic Library. Play now and expand your knowledge!",
        },
        {
          property: "og:url",
          content: "https://sw-holonet.com/games/holoword",
        },
        { property: "og:type", content: "website" },
        {
          name: "twitter:title",
          content: "Holoword – Daily Star Wars Word Puzzle Game",
        },
        {
          name: "twitter:description",
          content: "Guess the secret Star Wars word of the day in Holoword! Test your lore knowledge and help expand the Galactic Encyclopedia. Play now!",
        },
        { name: "twitter:card", content: "summary" },
        {
          name: "keywords",
          content: "Holoword, Star Wars word puzzle, daily Star Wars game, Star Wars lore, galactic word game, Bothan researcher, Star Wars trivia, Holonet game, Star Wars puzzle",
        },
      ],
    });
  },
  computed: {
  sortedGuessHistory() {
    // Use a Set to track unique words
    const uniqueWords = new Set();
    return this.guessHistory
      .filter(entry => {
        // Check if the word is already in the Set
        if (!uniqueWords.has(entry.word)) {
          uniqueWords.add(entry.word);
          return true;
        }
        return false;
      })
      .sort((a, b) => a.proximity - b.proximity)
      .reverse();
  }
  },
  mounted() {
    this.initializeGame(); // Automatically initialize the game when the component is mounted
  },
  methods: {
    async initializeGame() {
      try {
        const response = await apiClient.post("/api/holoword/init");
        this.initializationMessage = response.data.message;
        this.secretWord = "";
        this.rank = null;
        this.proximity = null;
        this.lastGuess = null;
        this.guessHistory = [];
        this.feedbackMessage = "";
        this.revealed = false;
      } catch (error) {
        console.error(error.response?.data?.error || "An error occurred.");
        this.initializationMessage = error.response?.data?.error || "Failed to initialize the game.";
      }
    },
    async submitGuess() {
      try {
        const response = await apiClient.post("/api/holoword/guess", { word: this.guess });
        this.rank = response.data.rank;
        this.proximity = response.data.proximity;
        this.proximity = Math.round(this.proximity * 100) / 100;
        const newGuess = {
          word: this.guess,
          rank: this.rank !== undefined ? this.rank : null,
          proximity: this.proximity !== undefined ? this.proximity : null
        };
        this.guessHistory.push(newGuess);
        this.lastGuess = newGuess; // Set the last guess
        // Clear inputs
        this.guess = "";
        this.feedbackMessage = "";
      } catch (error) {
        console.error(error.response.data.error);
        const failedGuess = {
          word: this.guess,
          rank: null,
          proximity: null
        };
        this.guessHistory.push(failedGuess);
        this.lastGuess = failedGuess; // Set the last guess even if it failed
        this.guess = "";
        this.feedbackMessage = error.response?.data?.error || "An error occurred.";
      }
      // Check if the user won
      if (this.rank === 1) {
        this.feedbackMessage = 'Congratulations! You found the secret word';
      }
    },
    revealSecret() {
      this.revealed = true;
      apiClient.get("/api/holoword/secret")
        .then(response => {
          this.secretWord = response.data;
        })
        .catch(error => {
          console.error("Error fetching secret:", error);
        });
    },
    getColor(rank) {
      if (rank === null) {
        return 'rgba(237, 27, 36, 0.7)'; // "Not in top 1000" with transparency
      }
      const ranges = [
        { min: 1, max: 1, color: 'rgba(106, 13, 173, 0.7)' },   // Dark Purple with transparency
        { min: 2, max: 25, color: 'rgba(40, 167, 69, 0.7)' },    // Muted Green with transparency
        { min: 26, max: 100, color: 'rgba(92, 184, 92, 0.7)' },  // Soft Green with transparency
        { min: 101, max: 250, color: 'rgba(240, 173, 78, 0.7)' }, // Soft Gold with transparency
        { min: 251, max: 500, color: 'rgba(217, 83, 79, 0.7)' },  // Muted Red with transparency
        { min: 501, max: 750, color: 'rgba(169, 68, 66, 0.7)' },  // Dark Red with transparency
        { min: 751, max: 1000, color: 'rgba(201, 48, 44, 0.7)' }  // Very Dark Red with transparency
      ];
      const range = ranges.find(r => rank >= r.min && rank <= r.max);
      return range ? range.color : 'rgba(237, 27, 36, 0.7)';
    }
  },
};
</script>
